import { QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BasePractitionerFragment } from 'generated/graphql'
import { AssessmentType } from '../../Clinical/Assessments/constants'

export interface AdapterOptions {
  /** Optional set of section names to filter by (for uncontrolled sections) */
  uncontrolledSections?: Set<string>
  /** Whether to preserve metadata fields during conversion */
  preserveMetadata?: boolean
  /** Care team data for fallback initial values */
  careTeam?: {
    caregivers?: BasePractitionerFragment[]
  }
  /** Caregiver timezone for date/time conversions */
  caregiverTimezone: string
}

export interface AssessmentAdapter<TFormData = Record<string, unknown>> {
  /** The assessment type this adapter handles */
  readonly assessmentType: AssessmentType

  /**
   * Converts FHIR QuestionnaireResponse to form data
   * @param questionnaireResponse - The FHIR resource to convert
   * @param options - Optional conversion settings
   * @returns Typed form data for React forms
   */
  toForm(questionnaireResponse: QuestionnaireResponse, options?: AdapterOptions): TFormData

  /**
   * Converts form data back to FHIR QuestionnaireResponse
   * @param formData - The form data to convert
   * @param existingFhir - Optional existing FHIR resource to merge with
   * @param options - Optional conversion settings
   * @returns Updated FHIR QuestionnaireResponse
   */
  toFHIR(
    formData: TFormData,
    existingFhir?: QuestionnaireResponse,
    options?: AdapterOptions
  ): QuestionnaireResponse
}

export class AdapterError extends Error {
  constructor(
    message: string,
    public readonly assessmentType?: AssessmentType,
    public readonly linkId?: string,
    public readonly originalError?: Error
  ) {
    super(message)
    this.name = 'AdapterError'
  }
}

/**
 * Common mutation structure for consistent flat answers building
 */
export interface FlatAnswerMutation {
  linkId: string
  value: unknown
}

/***
 * Base abstract class for all section adapters
 * Each section provides bidirectional transformation functions
 */
export abstract class SectionAdapter<T> {
  /**
   * Extract form data from flat FHIR answers for this subsection
   */
  abstract extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Partial<T>

  /**
   * Build flat FHIR answers from form data for this subsection
   */
  abstract buildFlatAnswers(formData: T): Array<FlatAnswerMutation>
}
